/*
 * -----------------------------------------------------------------------
 *  <copyright file="pager.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react';

import IPagerParams from './pager.interface';

import './styles.scss';

const controls = [5, 10, 25, 50, 100];

const Pager = ({
    page,
    pageSize,
    total,
    onChange,
    style,
    onSelectPageSize,
    amountOfPages,
    customClassname,
}: IPagerParams) => {
    const [pages, setPages] = useState([]);
    const [numPages, setNumPages] = useState(1);

    useEffect(() => {
        handleChangePageSize(pageSize, false);
    }, []);

    useEffect(() => {
        handleChangePageSize(pageSize, false);
    }, [pageSize, total]);

    useEffect(() => {
        handleChange(page, false);
    }, [page]);

    const handleChange = (value: number, triggerCallback: boolean = true) => {
        if (value === numPages && numPages >= amountOfPages) {
            // @ts-ignore
            setPages([...Array(numPages + 1).keys()].slice(value - amountOfPages + 1, value + 1));
        } else if (value > pages[pages.length - 1]) {
            const startIndex = value - amountOfPages + 1;
            // @ts-ignore
            setPages([...Array(numPages + 1).keys()].slice(startIndex, startIndex + amountOfPages));
        } else if (value < pages[0]) {
            // @ts-ignore
            setPages([...Array(numPages + 1).keys()].slice(value, value + amountOfPages));
        }
        if (onChange && triggerCallback) {
            onChange(value);
        }
    };

    const handleChangePageSize = (value: number, triggerCallback: boolean = true) => {
        let numPages = Math.floor((total - 1) / value) + 1;
        if (numPages === 0) {
            numPages = 1;
        }
        setNumPages(numPages);

        // @ts-ignore
        setPages([...Array(numPages + 1).keys()].slice(1, amountOfPages + 1));

        if (onSelectPageSize && triggerCallback) {
            onSelectPageSize(value);
        }
    };

    return (
        <div className={`pager ${customClassname}`} style={style}>
            <span className="records-title">{total} records &nbsp;&nbsp;</span>
            <div className="pager-wrapper">
                <div
                    className={page > 1 ? 'page-left full' : 'page-left full disabled'}
                    onClick={() => page > 1 && handleChange(1)}
                    data-testid="page-left-full"
                >
                    <span>|</span>
                    <FontAwesomeIcon icon={['fal', 'chevron-left']} />
                </div>
                <div
                    className={page > 1 ? 'page-left' : 'page-left disabled'}
                    onClick={() => page > 1 && handleChange(page - 1)}
                    data-testid="page-left"
                >
                    <FontAwesomeIcon icon={['fal', 'chevron-left']} />
                </div>

                {pages.map((p: number) => (
                    <div
                        key={p}
                        className={p === page ? 'page-number selected' : 'page-number'}
                        onClick={() => p !== page && handleChange(p)}
                        style={{ fontWeight: p === page ? 'bold' : 'normal' }}
                        data-testid={`page-number-${p}`}
                    >
                        {p}
                    </div>
                ))}

                <div
                    className={page < numPages ? 'page-right' : 'page-right disabled'}
                    onClick={() => page < numPages && handleChange(page + 1)}
                    data-testid="page-right"
                >
                    <FontAwesomeIcon icon={['fal', 'chevron-right']} />
                </div>
                <div
                    className={page < numPages ? 'page-right full' : 'page-right full disabled'}
                    onClick={() => page < numPages && handleChange(numPages)}
                    data-testid="page-right-full"
                >
                    <FontAwesomeIcon icon={['fal', 'chevron-right']} />
                    <span>|</span>
                </div>

                <div className="menu-wrapper">
                    <p className="show-text">Show</p>
                    <select
                        className="picker-menu"
                        value={pageSize}
                        onChange={(event) => {
                            onChange && onChange(1);
                            handleChangePageSize(+event.target.value);
                        }}
                        data-testid="page-size"
                    >
                        {controls.length &&
                            controls.map((item, index) => (
                                <option key={index} value={item}>
                                    {item}
                                </option>
                            ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

export default Pager;
